import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  ApiKeyCredentialIntegrationType,
  interpolateRoute,
  type Listing,
  type IntegrationSyncResult,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

/**
 * Tenant Turner Integration API Client
 * Handles all Tenant Turner-specific API operations
 */
export class TenantTurnerClient extends BaseApiClient {
  /**
   * Sync rental listings from Tenant Turner
   * Triggers sync and then fetches the synced listings
   */
  async syncListings(): Promise<Listing[]> {
    const result = await withErrorHandling(
      async () => {
        // Step 1: Trigger the sync (synchronous operation)
        const url = interpolateRoute(API_ROUTES.INTEGRATIONS.SYNC_LISTINGS, {
          integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER,
        })
        await this.request<ApiResponse<IntegrationSyncResult>>(url, {
          method: 'POST',
        })

        // Step 2: Fetch the synced listings
        const listingsResponse = await this.request<ApiResponse<Listing[]>>(
          API_ROUTES.LISTINGS.GET_ALL,
        )
        return listingsResponse.data ?? []
      },
      {
        errorContext: 'Error syncing Tenant Turner listings',
      },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Sync rental listings from Tenant Turner for a specific company
   * Note: This triggers the sync and then fetches the listings separately
   */
  async syncAdminListings(companyId: string): Promise<Listing[]> {
    const result = await withErrorHandling(
      async () => {
        // Step 1: Trigger the sync (synchronous operation)
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.SYNC_LISTINGS,
          { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
        )
        await this.request<ApiResponse<IntegrationSyncResult>>(
          `${url}?companyId=${companyId}`,
          { method: 'POST' },
        )

        // Step 2: Fetch the synced listings
        const listingsResponse = await this.request<ApiResponse<Listing[]>>(
          `${API_ROUTES.ADMIN.LISTINGS.GET_ALL}?companyId=${companyId}`,
        )
        return listingsResponse.data ?? []
      },
      {
        errorContext: 'Error syncing admin Tenant Turner listings',
      },
    )
    return result ?? []
  }

  /**
   * Create API key credentials for Tenant Turner
   */
  async createCredentials(
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
          { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ clientId, clientSecret }),
        })
      },
      {
        errorContext: 'Error creating Tenant Turner API key credentials',
      },
    )
  }

  /**
   * [ADMIN] Create API key credentials for Tenant Turner for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
          { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ clientId, clientSecret }),
          },
        )
      },
      {
        errorContext: 'Error creating admin Tenant Turner API key credentials',
      },
    )
  }

  /**
   * Update API key credentials for Tenant Turner
   */
  async updateCredentials(
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
          { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ clientId, clientSecret }),
        })
      },
      {
        errorContext: 'Error updating Tenant Turner API key credentials',
      },
    )
  }

  /**
   * [ADMIN] Update API key credentials for Tenant Turner for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
          { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ clientId, clientSecret }),
          },
        )
      },
      {
        errorContext: 'Error updating admin Tenant Turner API key credentials',
      },
    )
  }

  /**
   * Disconnect Tenant Turner integration
   * Uses the generic API key credentials disconnect endpoint
   */
  async disconnect(): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Tenant Turner integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Tenant Turner integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.TENANT_TURNER },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin Tenant Turner integration',
      },
    )
  }
}

// Export singleton instance
export const tenantTurnerClient = new TenantTurnerClient()
